import { Controller } from "@hotwired/stimulus";
import consumer from "../channels/consumer";

// Demo Call Controller - handles live demo calls from the landing page
// Connects to data-controller="demo-call"
export default class extends Controller {
  static targets = [
    "form",
    "callerLanguage",
    "countryCode",
    "destinationPhone",
    "destinationLanguage",
    "initiateButton",
    "endButton",
    "callStatus",
    "callStatusContainer",
    "timer",
    "transcript",
    "detectedLanguage",
    "remainingTime",
    "rateLimit",
    "rightPanel",
    "bookingPrompt",
    "inlineError",
    "inlineErrorText"
  ];

  static values = {
    demoCallId: String,
    maxSeconds: { type: Number, default: 120 },
    checkRateLimitUrl: String,
    createUrl: String
  };

  connect() {
    this.callActive = false;
    this.timerInterval = null;
    this.statusInterval = null;
    this.callStartTime = null;
    this.agentStreamStarted = false;

    this.checkRateLimit();
  }

  disconnect() {
    this.cleanup();
    this.stopAgentStream();
  }

  cleanup() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    if (this.statusInterval) {
      clearInterval(this.statusInterval);
      this.statusInterval = null;
    }
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  async checkRateLimit() {
    try {
      const response = await fetch(this.checkRateLimitUrlValue || "/demo_calls/check_rate_limit", {
        headers: { "Accept": "application/json" }
      });
      const data = await response.json();

      if (this.hasRemainingTimeTarget) {
        const remainingSeconds = typeof data.remaining_seconds === "number" ? data.remaining_seconds : 120;
        const minutes = Math.floor(remainingSeconds / 60);
        const seconds = remainingSeconds % 60;
        this.remainingTimeTarget.textContent = `${minutes}:${String(seconds).padStart(2, "0")}`;
      }

      if (this.hasInitiateButtonTarget) {
        this.initiateButtonTarget.disabled = !data.can_start;
        if (!data.can_start && data.resets_at) {
          const resetsAt = new Date(data.resets_at);
          const minutesUntilReset = Math.ceil((resetsAt - new Date()) / 60000);
          if (this.hasRateLimitTarget) {
            this.rateLimitTarget.textContent = `Rate limit reached. Try again in ${minutesUntilReset} minutes.`;
            this.rateLimitTarget.classList.remove("hidden");
          }
        }
      }
    } catch (error) {
      console.error("Failed to check rate limit:", error);
    }
  }

  async initiateCall(event) {
    event.preventDefault();

    // Clear any previous inline error
    this.clearInlineError();

    if (this.callActive) {
      console.warn("Call already active");
      return;
    }

    // Validate inputs
    const callerLanguage = this.hasCallerLanguageTarget ? this.callerLanguageTarget.value : "en";
    const countryCode = this.hasCountryCodeTarget ? this.countryCodeTarget.value : "31";
    const phoneNumber = this.hasDestinationPhoneTarget ? this.destinationPhoneTarget.value.trim() : "";
    const destinationLanguage = this.hasDestinationLanguageTarget ? this.destinationLanguageTarget.value : "auto";

    if (!phoneNumber) {
      this.showError("Please enter a phone number to call");
      return;
    }

    // Combine country code and phone number into E.164 format
    // Remove any non-digit characters from the phone number
    const cleanedPhone = phoneNumber.replace(/\D/g, "");
    const destinationPhone = `+${countryCode}${cleanedPhone}`;

    // Prime mic + AudioContext while we still have the user gesture. The
    // outbound call can take 10+ seconds to be answered, by which time the
    // gesture has expired in Safari/Chrome and getUserMedia would be denied
    // without an explicit click.
    try {
      await this.primeAgentAudio();
    } catch (err) {
      console.error("[demo-call] Microphone setup failed:", err);
      this.showError("Microphone access is required for the demo. Please allow it and try again.");
      return;
    }

    // Update UI to show connecting
    this.setCallState("connecting");

    // Get CSRF token
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content;

    try {
      const response = await fetch(this.createUrlValue || "/demo_calls", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({
          demo_call: {
            caller_language: callerLanguage,
            destination_phone_number: destinationPhone,
            destination_language: destinationLanguage
          }
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || "Failed to initiate call");
      }

      // Call initiated successfully
      this.demoCallIdValue = data.demo_call_id;
      this.agentStreamUrl = data.agent_stream_url;
      this.setCallState("ringing");
      this.subscribeToCallUpdates(data.demo_call_id);
      this.startStatusPolling(data.demo_call_id);

    } catch (error) {
      console.error("Failed to initiate call:", error);
      this.showError(error.message);
      this.setCallState("idle");
    }
  }

  async endCall(event) {
    if (event) event.preventDefault();

    if (!this.demoCallIdValue) {
      console.warn("No active call to end");
      return;
    }

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content;

    try {
      const response = await fetch(`/demo_calls/${this.demoCallIdValue}/end_call`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to end call");
      }

      this.handleCallEnded(data);

    } catch (error) {
      console.error("Failed to end call:", error);
      // Force end locally anyway
      this.handleCallEnded({ status: "completed" });
    }
  }

  subscribeToCallUpdates(demoCallId) {
    // Subscribe to demo call channel for real-time updates
    this.subscription = consumer.subscriptions.create(
      { channel: "DemoCallChannel", id: demoCallId },
      {
        connected: () => {
          // Connected to DemoCallChannel
        },
        disconnected: () => {
          // Disconnected from DemoCallChannel
        },
        received: (data) => {
          this.handleWebSocketMessage(data);
        }
      }
    );
  }

  startStatusPolling(demoCallId) {
    // Poll for status updates as backup for WebSocket
    this.statusInterval = setInterval(async () => {
      try {
        const response = await fetch(`/demo_calls/${demoCallId}/status`, {
          headers: { "Accept": "application/json" }
        });
        const data = await response.json();

        if (data.status === "in_progress" && !this.callActive) {
          this.setCallState("in_progress");
          this.startTimer();
          this.startAgentStream();
        } else if (data.status === "completed" || data.status === "failed") {
          this.handleCallEnded(data);
        }

        // Update detected language
        if (data.detected_language && this.hasDetectedLanguageTarget) {
          this.detectedLanguageTarget.textContent = this.formatLanguageName(data.detected_language);
        }

        // Update transcript
        if (data.transcript && data.transcript.length > 0) {
          this.renderTranscript(data.transcript);
        }

      } catch (error) {
        console.error("Status polling error:", error);
      }
    }, 2000);
  }

  handleWebSocketMessage(data) {
    switch (data.type) {
    case "call_started":
      this.setCallState("in_progress");
      this.startTimer();
      this.startAgentStream();
      break;
    case "transcript_partial":
      this.handleTranscriptPartial(data);
      break;
    case "transcript_final":
      this.handleTranscriptFinal(data);
      break;
    case "language_detected":
      if (this.hasDetectedLanguageTarget && data.language_code) {
        this.detectedLanguageTarget.textContent = this.formatLanguageName(data.language_code);
      }
      break;
    case "translation":
      this.handleTranslation(data);
      break;
    case "call_ended":
      this.handleCallEnded(data);
      break;
    }
  }

  setCallState(state) {
    const isActiveCall = ["connecting", "ringing", "in_progress"].includes(state);
    const showRightPanel = isActiveCall || state === "ended";

    // Update form visibility - hide when call is active
    if (this.hasFormTarget) {
      this.formTarget.classList.toggle("hidden", state !== "idle");
    }

    // Update initiate button - only show when idle
    if (this.hasInitiateButtonTarget) {
      this.initiateButtonTarget.classList.toggle("hidden", state !== "idle");
    }

    // Update call status container - show during active call states
    if (this.hasCallStatusContainerTarget) {
      this.callStatusContainerTarget.classList.toggle("hidden", state === "idle");
    }

    // Update status text
    if (this.hasCallStatusTarget) {
      const statusMessages = {
        idle: "Ready to call",
        connecting: "Connecting...",
        ringing: "Ringing...",
        in_progress: "Call in progress",
        ended: "Call ended"
      };
      this.callStatusTarget.textContent = statusMessages[state] || state;
    }

    // Update end button visibility - show during active call
    if (this.hasEndButtonTarget) {
      this.endButtonTarget.classList.toggle("hidden", !isActiveCall);
    }

    // Update right panel (transcription & actions) - show during call and after
    if (this.hasRightPanelTarget) {
      if (showRightPanel) {
        this.rightPanelTarget.classList.remove("hidden");
        this.rightPanelTarget.classList.add("flex");
      } else {
        this.rightPanelTarget.classList.add("hidden");
        this.rightPanelTarget.classList.remove("flex");
      }
    }

    if (this.hasBookingPromptTarget) {
      this.bookingPromptTarget.classList.toggle("hidden", state !== "ended");
    }

    this.callActive = isActiveCall;
  }

  startTimer() {
    if (this.timerInterval) return;

    this.callStartTime = Date.now();
    
    this.timerInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - this.callStartTime) / 1000);
      const minutes = Math.floor(elapsed / 60);
      const seconds = elapsed % 60;
      
      if (this.hasTimerTarget) {
        this.timerTarget.textContent = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
      }

      // Check if we've exceeded max time (safety check - server should handle this)
      if (elapsed >= this.maxSecondsValue) {
        console.warn("Max demo time reached, ending call");
        this.endCall();
      }
    }, 1000);
  }

  handleCallEnded(_data) {
    this.cleanup();
    this.stopAgentStream();
    this.setCallState("ended");
    this.demoCallIdValue = "";
    this.agentStreamUrl = null;
    
    // Refresh rate limit
    setTimeout(() => {
      this.checkRateLimit();
    }, 1000);

    // Keep the booking prompt visible long enough to create a clear next step.
    setTimeout(() => {
      this.setCallState("idle");
      if (this.hasTranscriptTarget) {
        this.transcriptTarget.innerHTML = "";
      }
      if (this.hasTimerTarget) {
        this.timerTarget.textContent = "00:00";
      }
    }, 30000);
  }

  handleTranscriptPartial(data) {
    if (!this.hasTranscriptTarget || !data.text) return;

    const speaker = data.speaker || "customer";
    const partialId = `partial-${speaker}-${data.sequence ?? "latest"}`;
    const partialElement = this.transcriptTarget.querySelector(`[data-partial-id="${partialId}"]`);

    if (!partialElement) {
      // Create new partial entry
      const isAgent = speaker === "agent";
      const speakerLabel = isAgent ? "Agent" : "Customer";
      const bgClass = isAgent ?
        "bg-blue-50 text-blue-900 border-blue-100 dark:bg-blue-900/40 dark:text-blue-100 dark:border-blue-800/70" :
        "bg-emerald-50 text-emerald-900 border-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-100 dark:border-emerald-800/70";

      const html = `
        <div class="transcript-entry ${speaker} mb-3 opacity-80" data-partial-id="${partialId}" data-speaker="${speaker}" ${data.sequence !== undefined ? `data-sequence="${data.sequence}"` : ""}>
          <div class="flex gap-2 items-start ${isAgent ? "justify-end" : "justify-start"}">
            <div class="flex-1 max-w-xs">
              <div class="mb-1">
                <span class="text-xs font-semibold text-gray-500 dark:text-gray-400">${speakerLabel}</span>
              </div>
              <div class="${bgClass} border rounded-2xl px-3 py-2">
                <p class="text-sm leading-relaxed">${this.escapeHtml(data.text)}</p>
              </div>
            </div>
          </div>
        </div>
      `;

      this.transcriptTarget.insertAdjacentHTML("beforeend", html);
    } else {
      // Update existing partial entry text
      const textElement = partialElement.querySelector("p");
      if (textElement) {
        textElement.textContent = data.text;
      }
    }

    this.transcriptTarget.scrollTop = this.transcriptTarget.scrollHeight;
  }

  handleTranscriptFinal(data) {
    if (!this.hasTranscriptTarget || !data.text) return;

    const speaker = data.speaker || "customer";

    // Remove any matching partial bubble for this speaker/sequence
    const partialId = `partial-${speaker}-${data.sequence ?? "latest"}`;
    const existingPartial = this.transcriptTarget.querySelector(`[data-partial-id="${partialId}"]`);
    if (existingPartial) existingPartial.remove();

    const isAgent = speaker === "agent";
    const speakerLabel = isAgent ? "Agent" : "Customer";
    const bgClass = isAgent ?
      "bg-blue-100 text-blue-900 border-blue-200 dark:bg-blue-900/60 dark:text-blue-100 dark:border-blue-800" :
      "bg-green-100 text-green-900 border-green-200 dark:bg-emerald-900/60 dark:text-emerald-100 dark:border-emerald-800";

    const html = `
      <div class="transcript-entry ${speaker} mb-3" data-speaker="${speaker}" data-sequence="${data.sequence ?? ""}">
        <div class="flex gap-2 items-start ${isAgent ? "justify-end" : "justify-start"}">
          <div class="flex-1 max-w-xs">
            <div class="mb-1">
              <span class="text-xs font-semibold text-gray-600 dark:text-gray-300">${speakerLabel}</span>
            </div>
            <div class="${bgClass} border rounded-2xl px-3 py-2 shadow-sm">
              <p class="text-sm leading-relaxed">${this.escapeHtml(data.text)}</p>
            </div>
          </div>
        </div>
      </div>
    `;

    this.transcriptTarget.insertAdjacentHTML("beforeend", html);
    this.transcriptTarget.scrollTop = this.transcriptTarget.scrollHeight;
  }

  renderTranscript(transcriptEntries) {
    if (!this.hasTranscriptTarget) return;

    // Only render if we have new entries (count final entries only, not partials)
    const currentCount = this.transcriptTarget.querySelectorAll(".transcript-entry:not([data-partial-id])").length;
    if (transcriptEntries.length <= currentCount) return;

    // Append new entries as finals (polling returns completed transcripts)
    for (let i = currentCount; i < transcriptEntries.length; i++) {
      const entry = transcriptEntries[i];
      const entrySequence = entry.sequence || entry["sequence"];
      this.handleTranscriptFinal({
        speaker: entry.speaker,
        text: entry.original_text || entry.text,
        sequence: entrySequence
      });
    }
  }

  handleTranslation(data) {
    if (!this.hasTranscriptTarget || data.sequence === undefined || data.sequence === null) return;

    // Find the transcript entry by sequence and speaker
    const speaker = data.speaker || "customer";
    let entry = this.transcriptTarget.querySelector(`[data-sequence="${data.sequence}"][data-speaker="${speaker}"]`);

    // If not found as final entry, try to find as partial entry
    if (!entry) {
      const partialId = `partial-${speaker}-${data.sequence}`;
      entry = this.transcriptTarget.querySelector(`[data-partial-id="${partialId}"]`);
    }

    if (!entry) return;

    const bubble = entry.querySelector(".rounded-2xl");
    if (!bubble) return;

    const isAgent = speaker === "agent";
    const originalText = data.original_text || "";
    const translatedText = data.translated_text || "";

    // Update the bubble content with original and translated text
    const textColorClass = isAgent ? "text-blue-900 dark:text-blue-100" : "text-green-900 dark:text-emerald-100";
    const borderColorClass = isAgent ? "border-blue-200 dark:border-blue-700" : "border-green-200 dark:border-green-700";

    if (translatedText && translatedText !== originalText) {
      bubble.innerHTML = `
        <div class="mb-2">
          <p class="text-sm leading-relaxed ${textColorClass}">${this.escapeHtml(originalText)}</p>
        </div>
        <div class="pt-2 border-t ${borderColorClass}">
          <p class="text-sm leading-relaxed text-gray-600 dark:text-gray-400">${this.escapeHtml(translatedText)}</p>
        </div>
      `;
    }

    this.transcriptTarget.scrollTop = this.transcriptTarget.scrollHeight;
  }

  showError(message) {
    // Show inline error (persistent until next attempt)
    if (this.hasInlineErrorTarget && this.hasInlineErrorTextTarget) {
      this.inlineErrorTextTarget.textContent = message;
      this.inlineErrorTarget.classList.remove("hidden");
    }

    // Also show toast notification
    const notification = document.createElement("div");
    notification.className = "fixed top-4 left-1/2 transform -translate-x-1/2 z-50 px-4 py-2 bg-red-500 text-white rounded-lg shadow-lg";
    notification.textContent = message;
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.remove();
    }, 5000);
  }

  clearInlineError() {
    if (this.hasInlineErrorTarget) {
      this.inlineErrorTarget.classList.add("hidden");
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  // ----- Agent audio stream (browser → FastAPI bidirectional WS) -----
  //
  // Captures the visitor's microphone, converts to 16 kHz PCM16 LE, and pushes
  // it to /webhooks/direct_stream as the "agent" leg. FastAPI's stream
  // registry pairs this with the Telnyx "customer" leg by call_id, so the
  // customer's translated audio comes back here as Twilio-shaped media events
  // (µ-law base64) which we decode + play through Web Audio.
  //
  // primeAgentAudio() must run inside the user's click handler — Safari and
  // Chrome both require AudioContext + getUserMedia to be triggered by a
  // fresh user gesture. The destination phone can take 10+ seconds to
  // answer, by which point the gesture is stale, so we set up the audio
  // graph eagerly and just open/route the WebSocket once the call answers.
  async primeAgentAudio() {
    if (this.audioContext && this.micStream) return; // already primed

    const Ctx = window.AudioContext || window.webkitAudioContext;
    this.audioContext = new Ctx({
      sampleRate: 16000,
      latencyHint: "interactive"
    });

    // Load worklet from public/ to avoid bundler inlining as data: URL (blocked by CSP)
    await this.audioContext.audioWorklet.addModule("/audio/agent_mic_processor.js");

    this.micStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
        sampleRate: 16000
      }
    });
  }

  async startAgentStream() {
    if (this.agentStreamStarted) return;
    if (!this.demoCallIdValue) return;

    // Signed by Rails; the ML API refuses an unsigned stream.
    const wsUrl = this.agentStreamUrl;
    if (!wsUrl) {
      console.warn("[demo-call] No agent stream URL for this demo; skipping agent mic stream");
      return;
    }

    if (!this.audioContext || !this.micStream) {
      console.warn("[demo-call] Agent audio was not primed; skipping mic stream");
      return;
    }

    this.agentStreamStarted = true;

    try {
      if (this.audioContext.state === "suspended") {
        try { await this.audioContext.resume(); } catch { /* ignore */ }
      }

      this.micSource = this.audioContext.createMediaStreamSource(this.micStream);
      this.micWorklet = new AudioWorkletNode(this.audioContext, "agent-mic-processor", {
        numberOfInputs: 1,
        numberOfOutputs: 0,
        channelCount: 1,
        channelCountMode: "explicit"
      });
      this.micSource.connect(this.micWorklet);

      this.agentWs = new WebSocket(wsUrl);
      this.agentWs.binaryType = "arraybuffer";
      this.playbackQueueTime = 0;

      this.agentWs.onopen = () => {
        const streamSid = `browser-${this.demoCallIdValue}`;
        const startMsg = {
          event: "start",
          streamSid,
          start: {
            callSid: this.demoCallIdValue,
            mediaFormat: {
              encoding: "linear16",
              sampleRate: 16000,
              channels: 1
            },
            customParameters: {
              call_id: this.demoCallIdValue,
              speaker: "agent",
              provider: "browser"
            }
          }
        };
        this.agentWs.send(JSON.stringify(startMsg));
        this.agentStreamSid = streamSid;

        this.micWorklet.port.onmessage = (event) => {
          if (this.agentWs.readyState !== WebSocket.OPEN) return;
          const payload = this.arrayBufferToBase64(event.data);
          this.agentWs.send(JSON.stringify({
            event: "media",
            streamSid: this.agentStreamSid,
            media: { payload }
          }));
        };
      };

      this.agentWs.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.event === "media") {
            this.playInboundAudio(msg.media?.payload);
          } else if (msg.event === "clear") {
            // FastAPI emits "clear" before each new TTS chunk run so old
            // playback can be dropped. Reset our scheduling cursor; the
            // already-scheduled sources will keep playing in the meantime,
            // but new chunks will queue from "now" rather than after them.
            this.playbackQueueTime = 0;
          }
        } catch (err) {
          console.warn("[demo-call] Bad agent WS message:", err);
        }
      };

      this.agentWs.onerror = (err) => {
        console.error("[demo-call] Agent WS error:", err);
      };

      this.agentWs.onclose = () => {
        this.agentStreamStarted = false;
      };

    } catch (err) {
      console.error("[demo-call] Failed to start agent stream:", err);
      this.agentStreamStarted = false;
      this.teardownAudioGraph();
    }
  }

  stopAgentStream() {
    if (this.agentWs && this.agentWs.readyState === WebSocket.OPEN) {
      try {
        this.agentWs.send(JSON.stringify({
          event: "stop",
          streamSid: this.agentStreamSid
        }));
      } catch {
        // ignore
      }
    }
    if (this.agentWs) {
      try { this.agentWs.close(); } catch { /* ignore */ }
      this.agentWs = null;
    }
    this.teardownAudioGraph();
    this.agentStreamStarted = false;
  }

  teardownAudioGraph() {
    if (this.micWorklet) {
      try { this.micWorklet.port.onmessage = null; } catch { /* ignore */ }
      try { this.micWorklet.disconnect(); } catch { /* ignore */ }
      this.micWorklet = null;
    }
    if (this.micSource) {
      try { this.micSource.disconnect(); } catch { /* ignore */ }
      this.micSource = null;
    }
    if (this.micStream) {
      this.micStream.getTracks().forEach(track => track.stop());
      this.micStream = null;
    }
    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
    this.playbackQueueTime = 0;
  }

  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const slice = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
      binary += String.fromCharCode.apply(null, slice);
    }
    return btoa(binary);
  }

  // Decode a Twilio-shaped media.payload (base64 µ-law @ 8 kHz mono) and
  // schedule it for back-to-back playback on the same AudioContext used for
  // mic capture. We keep a rolling "next start time" so adjacent chunks land
  // seamlessly without overlap or gaps.
  playInboundAudio(base64Payload) {
    if (!base64Payload || !this.audioContext) return;
    try {
      const binary = atob(base64Payload);
      const muLawBytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        muLawBytes[i] = binary.charCodeAt(i);
      }
      if (muLawBytes.length === 0) return;

      const float32 = new Float32Array(muLawBytes.length);
      for (let i = 0; i < muLawBytes.length; i++) {
        float32[i] = this.muLawDecodeSample(muLawBytes[i]);
      }

      const buffer = this.audioContext.createBuffer(1, float32.length, 8000);
      buffer.copyToChannel(float32, 0);

      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(this.audioContext.destination);

      const now = this.audioContext.currentTime;
      const startAt = Math.max(this.playbackQueueTime, now + 0.02);
      source.start(startAt);
      this.playbackQueueTime = startAt + buffer.duration;
    } catch (err) {
      console.warn("[demo-call] Failed to play inbound audio:", err);
    }
  }

  // ITU-T G.711 µ-law → linear PCM as a [-1, 1] float. Mirrors the table in
  // FastAPI's audio_utils.mulaw_to_pcm16 so the round-trip stays consistent.
  muLawDecodeSample(u_val) {
    u_val = ~u_val & 0xff;
    const sign = u_val & 0x80;
    const exponent = (u_val >> 4) & 0x07;
    const mantissa = u_val & 0x0f;
    let sample = ((mantissa << 3) + 0x84) << exponent;
    sample -= 0x84;
    if (sign !== 0) sample = -sample;
    return Math.max(-1, Math.min(1, sample / 32768));
  }

  formatLanguageName(langCode) {
    const languageMap = {
      "en": "English",
      "es": "Spanish",
      "fr": "French",
      "de": "German",
      "it": "Italian",
      "pt": "Portuguese",
      "ru": "Russian",
      "pl": "Polish",
      "nl": "Dutch",
      "sv": "Swedish",
      "da": "Danish",
      "no": "Norwegian",
      "fi": "Finnish",
      "cs": "Czech",
      "hu": "Hungarian",
      "ro": "Romanian",
      "bg": "Bulgarian",
      "hr": "Croatian",
      "sk": "Slovak",
      "sl": "Slovenian",
      "et": "Estonian",
      "lv": "Latvian",
      "lt": "Lithuanian",
      "el": "Greek",
      "tr": "Turkish",
      "ar": "Arabic",
      "zh": "Chinese",
      "ja": "Japanese",
      "ko": "Korean",
      "hi": "Hindi"
    };

    if (!langCode) return "Detecting...";
    const baseCode = langCode.toLowerCase().split("-")[0];
    return languageMap[baseCode] || langCode.toUpperCase();
  }
}
